import React, { useEffect } from "react";
import { MainTracker } from "../../../PixelTrackers/MainTracker";
import { clientConfig } from "../../../config";
import JourneyLayout from "./JourneyLayout";
import StepTracker, { StepLabelTitle } from "./StepTracker";
import * as translator from "../../../utils/translator";
import RevealWords, { words } from "../../../components/Reveal/RevealWords";
import { useFunnel } from "./shellContext";

const t = (language: string | undefined, key: string): string =>
  translator.translate(language, "default", "QuestionnairePage/" + key) || "";

const PENS_DESKTOP = `${clientConfig.IMAGES_URL}redesign_2025/care-plus/questionnaire/pens/pens.png`;
const BLOB_MOBILE = `${clientConfig.IMAGES_URL}redesign_2025/care-plus/questionnaire/blob/blob.png`;
const PENS_MOBILE = `${clientConfig.IMAGES_URL}redesign_2025/care-plus/questionnaire/pens-mobile/pens-mobile.png`;

const INTRO_REVEAL = {
  label: words(62, 100, 400, 5, 2),
  heading: words(200, 73, 292, 8, 3),
  steps: [
    {
      dot: { start: 625, duration: 313 },
      connector: { start: 1150, duration: 750 },
      label: words(688, 100, 400, 5, 2),
      title: words(812, 115, 458, 7, 3),
    },
    {
      dot: { start: 1875, duration: 313 },
      connector: { start: 2400, duration: 500 },
      label: words(1938, 100, 400, 5, 2),
      title: words(2062, 115, 458, 7, 3),
    },
    {
      dot: { start: 2900, duration: 312 },
      label: words(2987, 87, 350, 4, 1),
      title: words(3112, 125, 500, 5, 2),
    },
  ],
};

interface IntroScreenProps {
  onStart: () => void;
  language?: string;
  mention?: string;
}

const IntroScreen: React.FC<IntroScreenProps> = ({ onStart, language, mention }) => {
  const isWl3 = useFunnel() === "wl3";
  const reveal = isWl3 ? INTRO_REVEAL : null;
  useEffect(() => {
    MainTracker.track("custom", "Questionnaire Intro Viewed", { mention });
  }, [mention]);

  const steps = [
    { label: t(language, "intro_step1_label"), title: t(language, "intro_step1_title"), active: true },
    { label: t(language, "intro_step2_label"), title: t(language, "intro_step2_title"), active: false },
    // HL-12122: V3 (WL3) is not Care+ — its plan step has no Care+ name.
    { label: t(language, "intro_step3_label"), title: t(language, isWl3 ? "intro_step3_title_v3" : "intro_step3_title"), active: false },
  ];

  const renderSteps = (compact: boolean) => (
    <StepTracker
      compact={compact}
      steps={steps.map((step, i) => {
        const stepReveal = reveal?.steps[i];
        return {
          status: step.active ? "active" : "upcoming",
          reveal: stepReveal ? { dot: stepReveal.dot, connector: stepReveal.connector } : undefined,
          content: (
            <StepLabelTitle label={step.label} title={step.title} active={step.active} compact={compact} reveal={stepReveal} />
          ),
        };
      })}
    />
  );

  return (
    <JourneyLayout
      pageId="wl2_intro_page"
      ctaId="wl2_intro_start_button"
      label={reveal ? <RevealWords text={t(language, "intro_label")} timing={reveal.label} /> : t(language, "intro_label")}
      heading={reveal ? <RevealWords text={t(language, "intro_heading")} timing={reveal.heading} /> : t(language, "intro_heading")}
      renderSteps={renderSteps}
      ctaText={t(language, "intro_start_button")}
      onStart={onStart}
      ctaDesktopClassName="mt-5 2xl:mt-7 h-[52px] 2xl:h-[60px] w-full max-w-[300px] 2xl:max-w-[344.4px] rounded-[14px] bg-black text-white font-['Poppins'] font-medium text-[17px] 2xl:text-[19px] shadow-[0px_16px_12px_rgba(0,0,0,0.08),0px_6px_4px_rgba(0,0,0,0.08)] cursor-pointer hover:bg-[#222] active:scale-[0.98] transition"
      renderHeroDesktop={() => (
        <img
          src={PENS_DESKTOP}
          alt=""
          className="w-full h-auto max-h-[min(620px,calc(100vh-140px))] object-contain rounded-[28px] 2xl:rounded-[32px]"
          loading="eager"
        />
      )}
      renderHeroMobile={() => (
        <>
          <img src={BLOB_MOBILE} alt="" className="absolute inset-0 w-full h-full object-cover object-top" />
          <img
            src={PENS_MOBILE}
            alt=""
            className="absolute right-2 top-4 w-[70%] max-w-[300px] h-auto"
            loading="eager"
          />
        </>
      )}
    />
  );
};

export default IntroScreen;
